import { each, find } from 'lodash/fp';
import { getToken } from '@optimus-web/core';
import { DENSE_B2B_LIGHT_THEME } from '@optimus-web/theme';
import { ResourceState } from '@mews-commander/legacy-data-structures';
import { getColorFromResourceState } from '@mews-commander/utils';
import { FONT_FAMILY, LABEL_SCALE_BREAKPOINT, DATE_LEGEND_HEIGHT } from '../../constants';
import {
    line,
    drawType,
    getSpaceRectangle,
    drawSpaceRectangle,
    fitText,
    drawText,
    getSpaceBackground,
    drawCircle,
} from '../../utils/drawUtils';
import { roundTo5 } from '../../utils/mathUtils';
import { COLOR_TEXT } from '../../../../components/commander-ui/constants';
import { CursorPosition } from '../../types';

const TEXT_PADDING = 8;
const TEXT_LEFT_PADDING = 34;
const CIRCLE_PADDING = 16;

export default function SpaceLegend(fromTextCache: $TSFixMe) {
    let width = 0;

    return {
        render({
            ctx,
            yAxis,
            canvasHeight,
            scale,
            resources,
            resourceCategoryTypes,
            highlightedSpaces,
            resourceLegendWidth,
            spaceWidth,
            withLines,
            searchedSpace,
        }: $TSFixMe) {
            if (resourceLegendWidth === 0) {
                // hide legend
                return;
            }

            width = resourceLegendWidth;

            ctx.clearRect(0, 0, width, canvasHeight);

            const fontSize = 14 * scale;
            const textPadding = TEXT_PADDING * scale;
            const textLeftPadding = TEXT_LEFT_PADDING * scale;
            const circlePadding = CIRCLE_PADDING * scale;
            each((space) =>
                drawSpace({
                    ctx,
                    yAxis,
                    scale,
                    space,
                    spaceWidth,
                    fontSize,
                    textPadding,
                    textLeftPadding,
                    circlePadding,
                    highlightedSpaces,
                    withLines,
                    searchedSpace,
                })
            )(resources);

            const highlightedSpaceRoot = find({ isRoot: true })(highlightedSpaces);
            each((type) =>
                drawType({
                    ctx,
                    yAxis,
                    height: canvasHeight,
                    type,
                    width,
                    withLabel:
                        highlightedSpaceRoot &&
                        (highlightedSpaceRoot as $TSFixMe).resourceCategoryId === (type as $TSFixMe).id,
                })
            )(resourceCategoryTypes);

            line(
                ctx,
                width,
                0,
                width,
                canvasHeight,
                getToken('paletteBrandNight200')({ theme: DENSE_B2B_LIGHT_THEME })
            );
        },

        collides(position: CursorPosition) {
            return position.x <= width;
        },
    };

    function drawSpace({
        ctx,
        yAxis,
        scale,
        space,
        spaceWidth,
        fontSize,
        textPadding,
        textLeftPadding,
        circlePadding,
        highlightedSpaces,
        withLines,
        searchedSpace,
    }: $TSFixMe) {
        const rect = getSpaceRectangle(space, spaceWidth, yAxis);

        // stretch space if necessary to fill rest of space legend
        if (space.isLeaf && rect.x2 < width) {
            rect.x2 = width;
        }

        const background = getSpaceBackground(space, highlightedSpaces, searchedSpace);
        drawSpaceRectangle(
            ctx,
            rect.x1,
            rect.x2,
            rect.y1 + DATE_LEGEND_HEIGHT,
            rect.y2 + DATE_LEGEND_HEIGHT,
            withLines,
            background
        );

        line(ctx, rect.x1, rect.y1 + DATE_LEGEND_HEIGHT, rect.x1, rect.y2 + DATE_LEGEND_HEIGHT);

        if (!space.isSlot && scale > LABEL_SCALE_BREAKPOINT) {
            const startX = rect.x1 + (textLeftPadding as number);
            const endX = rect.x2 - textPadding;
            const y = rect.y1 + DATE_LEGEND_HEIGHT + (fontSize as number) + (textPadding as number);
            const textSpace = endX - startX;

            ctx.font = `${fontSize}px ${FONT_FAMILY}`;
            const { text, width: textWith } = fromTextCache(space.name, roundTo5(textSpace), () =>
                fitText(ctx, space.name, textSpace)
            );

            const color = getColorFromResourceState(ResourceState.getByName(space.state).value);

            drawText(ctx, text, startX, y, true, fontSize);

            const circleR = 5 * scale;
            const circleX = rect.x1 + circleR + (circlePadding as number);
            drawCircle(ctx, circleX, rect.y1 + DATE_LEGEND_HEIGHT + (circlePadding as number), circleR, color);

            const underlineY = y + 2 * scale;
            line(ctx, startX, underlineY, startX + (textWith as number), underlineY, COLOR_TEXT);
        }
    }
}
